import { featureFlagKeys } from 'feature-flags/constants/featureFlags'
import { useFeatureFlag } from 'feature-flags/hooks/useFeatureFlag/useFeatureFlag'
import type { DamFeatureFlagVariables } from '../../../types/types'

interface UseVideoFallbackImageProps {
	videoUrl: string
	width?: string
	height?: string
}

const DEFAULT_QUALITY = '20'

/**
 * Hook that generates a thumbnail URL for a video hosted in Adobe Experience Manager (AEM) Dynamic Media.
 * - `fmt` is omitted to take AEM's default format.
 */
export const useVideoFallbackImage = ({
	videoUrl,
	width,
	height,
}: UseVideoFallbackImageProps): string | undefined => {
	const { variables } = useFeatureFlag<DamFeatureFlagVariables>(
		featureFlagKeys.Dam
	)

	if (!variables.isVideoEnabled) {
		return videoUrl
	}

	try {
		const url = new URL(videoUrl)

		// If the URL does not contain '/is/content/', it's not a valid AEM video
		if (!url.pathname.includes('/is/content/')) {
			return undefined
		}

		const imageUrl = new URL(
			`${url.origin}/is/image${url.pathname.replace('/is/content', '')}`
		)

		const searchParams = new URLSearchParams({
			qlt: DEFAULT_QUALITY,
			...(width && { wid: width }),
			...(height && { hei: height }),
		})

		imageUrl.search = searchParams.toString()

		return imageUrl.toString()
	} catch (error) {
		console.error('Error generating the thumbnail URL:', error)
		return undefined
	}
}
